import { LoadingStatus, SearchContentSection, SearchResponseData } from '@api/types';
import { QuickSearchResultsItem, Title } from '@components';
import { useAppDispatch, useAppSelector, useInternalRedirect } from '@hooks';
import { Box, Skeleton, styled } from '@mui/material';
import { getSpecialPage } from '@store/app/app.slice';
import { setSearchSection } from '@store/search/search.slice';
import { palette } from '@themes/theme.helpers';
import { t } from '@translations';

// Components & styles
const ResultListBox = styled('ul')`
  padding-inline-start: 0;
  list-style-type: none;
`;

const SectionTitle = styled(Box)`
  font-size: 16px;
  font-weight: 700;
  line-height: 22px;
  letter-spacing: 0.01em;
  color: ${palette.secondary('dark')};
  text-decoration: underline !important;
  margin-bottom: 14px;
  display: block;
  cursor: pointer;
`;

export type SearchResultSectionProps = {
  title?: string;
  data?: SearchResponseData;
  status?: LoadingStatus;
  limit?: number;
  to?: string;
  showImages?: boolean;
  showPrices?: boolean;
  download?: boolean;
  section?: SearchContentSection;
};

export const SearchResultsSection = ({
  data,
  status = {},
  limit = 3,
  title = '',
  showImages,
  showPrices,
  section,
  download,
}: SearchResultSectionProps) => {
  const { isLoading, isFetching } = status;
  const isWorking = isLoading || isFetching;
  const translatedTitle = t(title, { totalCount: data?.totalCount?.toString() ?? '0' });

  // Redirect to Full Search Results
  const navigate = useInternalRedirect();
  const searchResultsPageUrl = useAppSelector(getSpecialPage('searchResultsPage'));
  const dispatch = useAppDispatch();
  const titleClickHandler = () => {
    if (searchResultsPageUrl && section) {
      navigate(searchResultsPageUrl);
      dispatch(setSearchSection(section));
    }
  };
  return (
    <ResultListBox>
      {isWorking && (
        <>
          <SectionTitle>{translatedTitle}</SectionTitle>
          <SearchResultsPlaceholder limit={1} />
        </>
      )}
      {!isWorking && data?.items && data?.items.map && (
        <>
          <SectionTitle onClick={titleClickHandler}>{translatedTitle}</SectionTitle>
          {data?.items.slice(0, limit).map((item: any, key: number) => (
            <QuickSearchResultsItem
              key={key}
              item={item}
              showImages={showImages}
              showPrices={showPrices}
              download={download}
              shouldRenderAsRouterLink
            />
          ))}
        </>
      )}
    </ResultListBox>
  );
};

export const SearchResultsPlaceholder = ({ limit = 3 }: SearchResultSectionProps) => {
  const items = new Array(limit);
  return (
    <>
      {[...items].map((_, key) => {
        return (
          <Title variant='h3' key={key} noDot>
            <Skeleton />
          </Title>
        );
      })}
    </>
  );
};
